import { useEffect, useRef, useState } from 'react';
import { User, Tag, FilmSlate } from '@phosphor-icons/react';
import ContextMenu from './ContextMenu';
import type { MenuGroup, MenuItem } from './ContextMenu';
import type { CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Message, Lesson, ReactionKey } from '../types/chat';
import { REACTION_KEYS, REACTION_EMOJI, REACTION_LABEL } from '../types/chat';
import type { Order } from '../types/order';
import VideoFrameThumb from './VideoFrameThumb';
import { formatExactTime } from '../utils/messageSessions';
import { lessonEventLabel } from '../utils/messageLabels';
import { capName } from '../utils/capName';
import { useUserIdentity } from '../hooks/useUserIdentity';
import { useStorageDownloadUrl } from '../hooks/useStorageDownloadUrl';

interface MessageBubbleProps {
  message: Message;
  isOwnMessage: boolean;
  isDoubleBagelSender?: boolean;
  showStaffAttribution?: boolean;
  lesson?: Lesson | null;
  /** Parent order of `lesson`. Lets the bubble label the system-event card
   *  by offering type (shot_consult / single_lesson / training_program). */
  lessonOrder?: Order | null;
  /** true when the message sender is a coach (used for avatar color) */
  isCoachSender?: boolean;
  /** Show avatar (last message in a same-sender streak). Defaults to true. */
  showAvatar?: boolean;
  onReply?: (message: Message) => void;
  /** Opens the reply thread in focus mode. Tapping the ghost is how you get
   *  back to the message being replied to. */
  onOpenThread?: (messageId: string) => void;
  onReplyWithVideoEdit?: (message: Message) => void;
  onOpenLesson?: (lesson: Lesson) => void;
  /** Called when the sender's avatar is tapped — set only when the sender's profile is reachable (e.g. coach). */
  onAvatarClick?: () => void;
  /** Whether the current user is allowed to edit this message. */
  canEdit?: boolean;
  /** Whether the current user is allowed to delete this message. */
  canDelete?: boolean;
  /** When true, the bubble renders an inline edit textarea instead of message text. */
  isEditing?: boolean;
  /** Called when the user clicks "Edit" in the action menu. */
  onEdit?: (message: Message) => void;
  /** Called when the user clicks "Delete" in the action menu. */
  onDelete?: (message: Message) => void;
  /** Called with the trimmed new text when the user saves an inline edit. */
  onEditSave?: (newText: string) => void;
  /** Called when the user cancels an in-progress edit. */
  onEditCancel?: () => void;
  /** The viewer's uid. Needed to pick their own Tapback out of the map. */
  currentUserId?: string;
  /** Called with the chosen Tapback, or null when the user clears their own. */
  onReact?: (message: Message, reaction: ReactionKey | null) => void;
  /** How many messages reply to this one. Counted by ConversationView. */
  replyCount?: number;
  /**
   * Suppresses the ghost, its hook, and the "N Replies" label. Focus mode
   * shows the thread as plain bubbles; those two are normal-thread
   * decorations that would restate what the overlay already says.
   */
  hideReplyChrome?: boolean;
  /**
   * The live parent this message replies to, resolved from the thread rather
   * than from the `replyTo*` fields cached at send time. Those go stale on an
   * edit and say nothing about a delete; the whole thread is in memory, so the
   * real message is available. Falls back to the cache when absent.
   */
  replyParent?: Message | null;
}

/**
 * Note bubble shown above a system-event card (lesson submitted / delivered)
 * as part of a clustered pair. Renders like a regular chat text bubble
 * (own = navy, received = white) so it reads as the author's narrative.
 *
 * Long notes (~6 lines / 280 chars) default to truncated with a subtle
 * "Show more" toggle. The bubble is the author's voice — not styled as
 * a system event card.
 */
const NOTE_TRUNCATE_AT = 280;

function NoteBubble({
  text,
  isOwnMessage,
}: {
  text: string;
  isOwnMessage: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const trimmed = text.trim();
  const needsTruncation = trimmed.length > NOTE_TRUNCATE_AT;
  const visibleText = needsTruncation && !expanded
    ? trimmed.slice(0, NOTE_TRUNCATE_AT).trimEnd() + '…'
    : trimmed;

  return (
    /* Standard bubble treatment. No tail: this sits inside a cluster with a
       card beneath it, so it is never the last message in a run. */
    <div
      className="max-w-[70%]"
      style={{
        borderRadius: 18,
        padding: '6px 12px',
        background: isOwnMessage ? '#1A6B8A' : '#E9E9EB',
        color: isOwnMessage ? '#FFFFFF' : '#000000',
      }}
    >
      <p className="text-[17px] leading-[22px] md:text-[13px] md:leading-[18px] whitespace-pre-wrap break-words">
        {visibleText}
      </p>
      {needsTruncation && (
        <button
          type="button"
          onClick={() => setExpanded(v => !v)}
          className={`mt-1 text-[12px] leading-[16px] font-medium hover:opacity-80 transition-opacity ${
            isOwnMessage ? 'text-white/80' : 'text-biscayne'
          }`}
        >
          {expanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </div>
  );
}

/**
 * Small sender chip rendered once at the top of a note-cluster on the
 * received side. Anchors the cluster's authorship without repeating the
 * avatar inside the bubble or the system card.
 */
function ClusterSenderChip({
  avatarUrl,
  name,
  onClick,
}: {
  avatarUrl?: string;
  name: string;
  isCoachSender?: boolean;
  onClick?: () => void;
}) {
  const firstName = name.split(' ').filter(Boolean)[0] || name;
  const initial = (firstName || '?').charAt(0).toUpperCase();
  // All initials-fallback avatars use db-oasis app-wide so the rule is
  // "never `biscayne`" — guarantees the fallback never blends into the
  // banner background. Coach/player visual distinction in this slot is
  // intentionally dropped; the cluster's surrounding context (own vs
  // received alignment, name label) carries that signal instead.
  const dotColor = '#54C3EA';
  const content = (
    <>
      <div
        className="w-5 h-5 rounded-full overflow-hidden flex items-center justify-center flex-shrink-0"
        style={{ background: dotColor }}
      >
        {avatarUrl ? (
          <img src={avatarUrl} alt={firstName} className="w-full h-full object-cover" />
        ) : (
          <span className="text-[9px] font-bold text-white">{initial}</span>
        )}
      </div>
      <span className="text-[11px] font-semibold text-db-text-2">{firstName}</span>
    </>
  );
  if (!onClick) {
    return <div className="flex items-center gap-1.5 px-1">{content}</div>;
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center gap-1.5 px-1 hover:opacity-80 transition-opacity"
      aria-label={`View ${firstName}'s profile`}
    >
      {content}
    </button>
  );
}

// Hover-reveal exact-time label — web analog of iOS swipe-to-reveal.
// Absolutely positioned just outside the bubble so it doesn't reflow content
// when it fades in. On desktop the group-hover pathway shows it; on touch
// the long-press isRevealed pathway forces it visible (touch devices have
// no hover state). Day context comes from the centered session-header
// divider that ConversationView renders above each group.
function HoverTime({
  iso,
  isOwnMessage,
  isRevealed = false,
}: {
  iso: string;
  isOwnMessage: boolean;
  isRevealed?: boolean;
}) {
  return (
    <span
      aria-hidden="true"
      className={`pointer-events-none absolute top-1/2 -translate-y-1/2 text-[10px] text-db-text-3 whitespace-nowrap transition-opacity duration-150 ${
        isOwnMessage ? 'right-full mr-2' : 'left-full ml-2'
      } ${
        isRevealed ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
      }`}
    >
      {formatExactTime(new Date(iso))}
    </span>
  );
}

/**
 * Format a video duration (seconds) as a compact label for the delivered
 * card's duration badge. Returns empty string when duration is missing.
 */
function formatVideoDuration(sec?: number | null): string {
  if (sec === undefined || sec === null || sec <= 0) return '';
  const total = Math.round(sec);
  const m = Math.floor(total / 60);
  const s = total % 60;
  if (m === 0) return `${s}s`;
  if (s === 0) return `${m} min`;
  return `${m}m ${s}s`;
}

/**
 * Format a relative time (e.g., "2 min ago", "3h ago", "yesterday").
 * Used by the coach-side submitted card to anchor when the player sent.
 */
function formatRelativeTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const diffMs = Date.now() - date.getTime();
  if (diffMs < 60_000) return 'just now';
  const m = Math.floor(diffMs / 60_000);
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d === 1) return 'yesterday';
  return `${d}d ago`;
}

/**
 * The six Tapbacks in Apple's order, as a row across the top of the context
 * menu. Each is a 36px circular target — under the 44 minimum for a standalone
 * control, but this is a tight cluster of six inside an already-committed
 * gesture, which is how iOS sizes its own Tapback bar.
 *
 * The viewer's current reaction is filled; tapping it again clears it, which is
 * what makes the row a toggle rather than a picker.
 */
function ReactionPicker({ current, onPick }: {
  current: ReactionKey | null;
  onPick: (reaction: ReactionKey | null) => void;
}) {
  return (
    // No horizontal padding of its own: the Mac treatment gives the menu 8px
    // padding and a 1px border, leaving 222 inside a 240 menu, and six 36px
    // targets need 216. justify-between distributes whatever is left over.
    <div className="flex items-center justify-between" style={{ padding: '6px 0' }}>
      {REACTION_KEYS.map((key) => {
        const active = current === key;
        return (
          <button
            key={key}
            type="button"
            role="menuitem"
            aria-pressed={active}
            aria-label={active ? `Remove ${REACTION_LABEL[key]} reaction` : `React with ${REACTION_LABEL[key]}`}
            onClick={() => onPick(active ? null : key)}
            className="ios-press shrink-0 flex items-center justify-center rounded-full transition-colors"
            style={{
              width: 36,
              height: 36,
              fontSize: 20,
              lineHeight: '20px',
              background: active ? '#1A6B8A' : 'transparent',
            }}
          >
            <span aria-hidden="true">{REACTION_EMOJI[key]}</span>
          </button>
        );
      })}
    </div>
  );
}

/**
 * The Tapback pill on the bubble. iOS hangs it off the top corner on the
 * outward-facing side — away from the screen edge, so outgoing messages carry
 * it top-left and incoming top-right — overlapping the bubble rather than
 * displacing it.
 *
 * A single reaction is a 26px circle. More than one and it grows into a
 * capsule: one emoji per distinct reaction, in Apple's order so the pill does
 * not reshuffle as people react. It grows inward, toward the middle of the
 * thread, because the outward edge is the one anchored to the bubble corner.
 */
const PILL_H = 26;
/** How far the pill hangs past the bubble corner. A third of it overlaps. */
const PILL_OVERHANG = Math.round(PILL_H * (2 / 3));

function ReactionPill({ reactions, isOwnMessage }: {
  reactions: Record<string, ReactionKey>;
  isOwnMessage: boolean;
}) {
  const entries = Object.values(reactions);
  if (entries.length === 0) return null;

  const distinct = REACTION_KEYS.filter(k => entries.includes(k));
  const isCapsule = distinct.length > 1;

  return (
    <div
      className="absolute flex items-center justify-center pointer-events-none"
      style={{
        // Outgoing sits on the right of the thread, so its pill hangs off the
        // left, and the reverse for incoming. Anchoring the outward side means
        // the capsule grows inward and the corner stays put.
        [isOwnMessage ? 'left' : 'right']: -PILL_OVERHANG,
        top: -14,
        height: PILL_H,
        minWidth: PILL_H,
        gap: 3,
        padding: isCapsule ? '0 7px' : 0,
        borderRadius: 100,
        background: '#FFFFFF',
        // A 2px white ring, not a hairline: it cuts the pill out of the bubble
        // beneath so it reads as lifted rather than drawn on.
        boxShadow: '0 0 0 2px #FFFFFF, 0 1px 3px rgba(0, 0, 0, 0.12)',
        zIndex: 2,
      }}
    >
      {distinct.map(k => (
        <span key={k} style={{ fontSize: 14, lineHeight: '14px' }}>{REACTION_EMOJI[k]}</span>
      ))}
      {/* A count only where it says something the emoji cannot: several people
          on the same reaction. */}
      {entries.length > distinct.length && (
        <span className="text-[11px] leading-[14px] font-medium text-[rgba(60,60,67,0.6)]">
          {entries.length}
        </span>
      )}
    </div>
  );
}

/**
 * Long-press hook tuned for chat bubbles. Trigger after 500ms hold, then keep
 * the revealed state for ~1.5s so the user can read the timestamp before it
 * fades.
 *
 * Cancellation is what keeps this from firing mid-scroll, and it has three
 * independent guards, because any one alone leaks:
 *
 *   1. Movement past MOVE_TOLERANCE_PX from the touch origin. A flick that
 *      starts on a bubble is a scroll, not a press. 10px is roughly the same
 *      slop UIKit allows before it treats a touch as a drag.
 *   2. `onTouchEnd` / `onTouchCancel`, for a tap that lifts before 500ms.
 *   3. A capture-phase `scroll` listener on window. Momentum scrolling can move
 *      the page without emitting further touchmove events once the finger has
 *      lifted, so movement alone does not catch every case.
 *
 * `onLongPress` fires only if none of the three cancelled first.
 */
const MOVE_TOLERANCE_PX = 10;

function useBubbleLongPress(onLongPress?: (x: number, y: number) => void) {
  const [isRevealed, setIsRevealed] = useState(false);
  const triggerTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const originRef = useRef<{ x: number; y: number } | null>(null);
  const firedRef = useRef(false);

  const cancelTrigger = () => {
    if (triggerTimerRef.current) {
      clearTimeout(triggerTimerRef.current);
      triggerTimerRef.current = null;
    }
    originRef.current = null;
  };

  // Guard 3: any scroll while the timer is armed kills it. Capture phase so it
  // sees scrolls on the message container, not just on window.
  useEffect(() => {
    const onScroll = () => cancelTrigger();
    window.addEventListener('scroll', onScroll, true);
    return () => window.removeEventListener('scroll', onScroll, true);
  }, []);

  const handlers = {
    onTouchStart: (e: React.TouchEvent) => {
      cancelTrigger();
      firedRef.current = false;
      const t = e.touches[0];
      originRef.current = t ? { x: t.clientX, y: t.clientY } : null;
      triggerTimerRef.current = setTimeout(() => {
        triggerTimerRef.current = null;
        setIsRevealed(true);
        if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
        hideTimerRef.current = setTimeout(() => setIsRevealed(false), 1500);
        if (onLongPress && originRef.current) {
          firedRef.current = true;
          onLongPress(originRef.current.x, originRef.current.y);
        }
      }, 500);
    },
    // Guard 1: movement past the tolerance means this is a scroll.
    onTouchMove: (e: React.TouchEvent) => {
      const o = originRef.current;
      const t = e.touches[0];
      if (!o || !t) return cancelTrigger();
      if (Math.abs(t.clientX - o.x) > MOVE_TOLERANCE_PX
        || Math.abs(t.clientY - o.y) > MOVE_TOLERANCE_PX) {
        cancelTrigger();
      }
    },
    // Guard 2: lifted before the timer elapsed.
    onTouchEnd: cancelTrigger,
    onTouchCancel: cancelTrigger,
  };

  // Suppress iOS Safari's native long-press context menu without killing
  // text selection globally on desktop.
  const style: CSSProperties = { WebkitTouchCallout: 'none' };

  useEffect(() => () => {
    if (triggerTimerRef.current) clearTimeout(triggerTimerRef.current);
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
  }, []);

  return { isRevealed, handlers, style, didFireRef: firedRef };
}

const downloadVideo = async (videoUrl: string, filename: string) => {
  try {
    const response = await fetch(videoUrl);
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  } catch (error) {
    console.error('Error downloading video:', error);
    alert('Failed to download video. Please try again.');
  }
};

// Lightweight video modal — opens player video fullscreen overlay
function VideoModal({ videoUrl, onClose }: { videoUrl: string; onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: 'rgba(0,0,0,0.85)' }}
      onClick={onClose}
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 w-10 h-10 rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-colors"
        style={{ background: 'rgba(255,255,255,0.1)' }}
        aria-label="Close"
      >
        <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
          <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
        </svg>
      </button>
      <video
        src={videoUrl}
        controls
        autoPlay
        controlsList="nodownload"
        className="max-w-[90vw] max-h-[85vh] rounded-lg"
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );
}

export default function MessageBubble({
  message,
  isOwnMessage,
  isDoubleBagelSender,
  showStaffAttribution,
  lesson,
  lessonOrder,
  isCoachSender,
  onReply,
  onOpenThread,
  onReplyWithVideoEdit,
  onOpenLesson,
  onAvatarClick,
  showAvatar = true,
  canEdit,
  canDelete,
  isEditing,
  onEdit,
  onDelete,
  onEditSave,
  onEditCancel,
  currentUserId,
  onReact,
  replyCount = 0,
  replyParent,
  hideReplyChrome = false,
}: MessageBubbleProps) {
  const navigate = useNavigate();
  const [videoModalUrl, setVideoModalUrl] = useState<string | null>(null);
  const [draftText, setDraftText] = useState('');

  // The context menu is anchored to a viewport point, not to the bubble's box:
  // three ancestors clip it if it renders in place — TwoColumnLayout's two
  // overflow-hidden panes and ConversationView's scrolling message list.
  // ContextMenu portals to document.body and handles placement, flipping,
  // focus and dismissal; this file only decides where and what.
  const [menuAnchor, setMenuAnchor] = useState<{ x: number; y: number } | null>(null);

  // The bubble is the menu's owner for focus purposes: focus goes into the
  // menu on open and comes back here on close. tabIndex -1 makes it a target
  // for focus() without adding a tab stop to the thread.
  const bubbleRef = useRef<HTMLDivElement>(null);
  const menuHostRef = useRef<HTMLDivElement>(null);

  // Reactions are available on any real message, including your own — iOS lets
  // you Tapback your own. Deleted tombstones and system events are excluded.
  const canReact = !!onReact && !!currentUserId && !message.isDeleted && message.type !== 'system';
  const myReaction: ReactionKey | null = (currentUserId && message.reactions?.[currentUserId]) || null;

  const hasMenuActions = canReact
    || !!onReply || (canEdit && onEdit) || (canDelete && onDelete);

  // Closing returns focus to the bubble, but only if focus is still inside the
  // menu. When an action has already moved it — Reply putting the caret in the
  // composer, Edit into the textarea — pulling it back here would undo that.
  const closeMenu = () => {
    const focusWasInMenu = !!menuHostRef.current?.contains(document.activeElement);
    setMenuAnchor(null);
    if (focusWasInMenu) bubbleRef.current?.focus();
  };

  const openMenuAt = (x: number, y: number) => {
    if (!hasMenuActions || isEditing) return;
    setMenuAnchor({ x, y });
  };

  // Pointer devices: right-click, the macOS Messages convention.
  const handleContextMenu = (e: React.MouseEvent) => {
    if (!hasMenuActions || isEditing) return;
    e.preventDefault();
    openMenuAt(e.clientX, e.clientY);
  };

  // Long-press reveal state. On touch, holding the bubble for 500ms surfaces
  // the exact-time label and opens the context menu; the time label fades back
  // out after a short reading window. Desktop hover behavior on the time label
  // is preserved via HoverTime's group-hover pathway.
  const {
    isRevealed, handlers: longPressHandlers, style: longPressStyle, didFireRef,
  } = useBubbleLongPress(openMenuAt);

  // iOS Safari still synthesises a click on touchend after a long press. Left
  // alone it would fall through to whatever the bubble does on tap — opening
  // the video modal behind the menu, for one. Swallow exactly one click after
  // the gesture fires; the flag is reset on the next touchstart either way.
  const swallowPostPressClick = (e: React.MouseEvent) => {
    if (!didFireRef.current) return;
    didFireRef.current = false;
    e.preventDefault();
    e.stopPropagation();
  };

  // A pill hangs 14px above the bubble. Without matching space it lands on the
  // message above, and inside a same-sender streak — where ConversationView
  // pulls bubbles together with a negative margin — it collides outright.
  const hasReaction = !!message.reactions && Object.keys(message.reactions).length > 0;
  // The reply-count label is absolutely placed under the bubble so it lands on
  // the bubble's own left edge rather than the row's, which is what "left
  // aligned under it" means for an outgoing bubble. Absolute means it reserves
  // no height, so the wrapper pays for it here.
  const bubbleStyle: CSSProperties = {
    ...longPressStyle,
    ...(hasReaction ? { marginTop: 14 } : null),
    ...(replyCount > 0 && !hideReplyChrome ? { marginBottom: 18 } : null),
  };

  // Seed the draft textarea with the current text when entering edit mode.
  useEffect(() => {
    if (isEditing) setDraftText(message.text ?? '');
  }, [isEditing, message.text]);

  // Live identity lookups — sender (for chat-row avatar chips) and coach
  // (for lesson-delivered hero card). Returns null while loading; callers
  // fall back to the message.sender* / lesson.coach* / order.coach*
  // snapshots so first paint is instant.
  const liveSender = useUserIdentity(message.senderId, isCoachSender ? 'coach' : 'player');
  const lessonCoachUid = lesson?.coachId ?? lessonOrder?.coachId ?? message.senderId;
  const liveLessonCoach = useUserIdentity(lessonCoachUid, 'coach');
  const liveLessonPlayer = useUserIdentity(lessonOrder?.playerId, 'player');

  // Pre-generated server-side thumbnail for lesson_delivered cards.
  // Uses the first frame from the thumbnail strip written by the video preprocessor.
  // Falls back to client-side VideoFrameThumb extraction when not available.
  const thumbBasePath = lesson?.playerVideoThumbnailsBasePath;
  const preGenThumbPath = thumbBasePath && (lesson?.playerVideoThumbnailCount ?? 0) > 0
    ? `${thumbBasePath}/0.jpg`
    : null;
  const preGenThumbUrl = useStorageDownloadUrl(preGenThumbPath);

  // Render replied-to message preview
  /**
   * The ghost copy of the message being replied to, plus the connector that
   * drops into the reply bubble.
   *
   * The ghost aligns to the PARENT's sender, not the reply's, so when the two
   * differ the pair straddles the pane. That is why this renders as a
   * full-width row above the message row rather than inside the bubble's
   * wrapper: the wrapper shrink-wraps to the bubble and sits on one side, so
   * nothing inside it can align to the other.
   *
   * The stub below the ghost is a tail, not a connector. Nothing links the
   * ghost to the reply bubble.
   *
   * Text comes from the live parent when ConversationView could resolve it,
   * and from the fields cached at send time otherwise. That is what makes an
   * edited parent read correctly and a deleted one say so.
   */
  const renderReplyGhost = () => {
    if (hideReplyChrome || !message.replyToMessageId) return null;

    const parent = replyParent;
    // Which side the ghost hangs on, and therefore which corner the stub
    // hangs off. With no resolvable parent there is no way to know, so it
    // falls back to this message's own side.
    const parentIsOwn = parent && currentUserId
      ? parent.senderId === currentUserId
      : isOwnMessage;

    let ghostText: string;
    if (parent?.isDeleted) {
      ghostText = 'Message deleted';
    } else if (parent?.text) {
      ghostText = parent.text;
    } else if (message.replyToText) {
      ghostText = message.replyToText;
    } else {
      // A parent with no text of its own: a video, a lesson card, a drill.
      // Name the kind rather than showing an empty ghost.
      const kind = parent?.type ?? message.replyToType;
      ghostText = kind === 'video' ? 'Video'
        : kind === 'image' ? 'Photo'
        : kind === 'drill_attachment' ? 'Drill'
        : kind === 'lesson_request' ? 'Lesson request'
        : kind === 'lesson_delivered' ? 'Delivered lesson'
        : 'Message';
    }

    const handleClick = () => {
      if (onOpenThread && message.replyToMessageId) {
        onOpenThread(message.replyToMessageId);
      }
    };

    // The ghost is drawn in the parent's own colour, not always grey: an
    // outgoing parent reads as a faded version of its own bubble.
    //
    // [INFERENCE] Ryan specified "light biscayne" without values. 0.3 is the
    // alpha the booking screen's upload zone already uses for a biscayne
    // hairline; 0.5 on the text is the same alpha the incoming ghost uses for
    // its grey, so the two sides carry equal weight and differ only in hue.
    const ghostStroke = parentIsOwn ? 'rgba(26, 107, 138, 0.3)' : 'rgba(0, 0, 0, 0.12)';
    const ghostTextColor = parentIsOwn ? 'rgba(26, 107, 138, 0.5)' : 'rgba(60, 60, 67, 0.5)';
    const ghostTail = parentIsOwn ? 'rgba(26, 107, 138, 0.3)' : '#D8D8D8';
    const HAIRLINE = `1px solid ${ghostStroke}`;
    const TAIL = `2.5px solid ${ghostTail}`;
    const R = 20;

    // The stub. A single quarter arc hanging off the ghost's bottom outer
    // corner that simply tails off — it does not reach the reply and does not
    // cross the pane, because iMessage does not connect the two.
    //
    // A box whose border-radius equals its own size is one quarter arc: the
    // stroke enters at one corner and leaves at the diagonally opposite one.
    // Both adjacent borders have to be set or CSS draws only half of it.
    // The tail hooks INWARD, toward the middle of the pane: an incoming ghost
    // goes down then right, an outgoing one down then left. So the stroke
    // enters vertically at the top and leaves horizontally pointing inward,
    // which for incoming is the bottom-LEFT radius. Outgoing mirrors.
    const stub = (
      <div
        aria-hidden="true"
        className="absolute"
        style={{
          top: '100%',
          ...(parentIsOwn ? { right: 0 } : { left: 0 }),
          width: R,
          height: R,
          borderBottom: TAIL,
          ...(parentIsOwn
            ? { borderRight: TAIL, borderBottomRightRadius: R }
            : { borderLeft: TAIL, borderBottomLeftRadius: R }),
        }}
      />
    );

    return (
      <div
        className={`flex ${parentIsOwn ? 'justify-end' : 'justify-start'}`}
        // 12 above, on top of the list's own 8px gap, so a reply pair opens
        // with 20 of clearance against the 8 between two ordinary messages and
        // the 2 inside a streak. Enough to read as the start of a new unit.
        style={{ marginTop: 12, marginBottom: R + 4 }}
      >
          <div
            onClick={handleClick}
            className={`relative w-fit ${onOpenThread ? 'cursor-pointer' : ''}`}
            style={{
              maxWidth: '70%',
              borderRadius: 18,
              border: HAIRLINE,
              background: 'transparent',
              color: ghostTextColor,
              // 85% of the bubble's own 6px/12px, rounded to whole pixels.
              padding: '5px 10px',
            }}
            title={onOpenThread ? 'View thread' : undefined}
          >
            {/* 85% of the bubble's 17/22 and 13/18, at the same md breakpoint.
                Clamped to 3 lines: a long parent would otherwise produce a
                ghost taller than the reply it belongs to. */}
            <p
              className="text-[14.5px] leading-[19px] md:text-[11px] md:leading-[15px] whitespace-pre-wrap break-words"
              style={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
            >
              {ghostText}
            </p>
            {stub}
          </div>
      </div>
    );
  };

  /**
   * The swipe-reveal timestamp. Anchored to the message row, not to the outer
   * row, so a reply ghost above the bubble no longer drags it out of line.
   *
   * The remaining error is arithmetic rather than measured: the bubble wrapper
   * carries a top margin when a reaction pill overhangs it and a bottom margin
   * when the reply-count label sits under it, which moves the row's centre off
   * the bubble's by exactly half their difference. Both are known constants
   * here, so the offset is exact and needs no measurement.
   *
   * Hidden on pointer devices — there is no gesture to reveal it there.
   */
  const renderRevealTime = () => {
    const mt = hasReaction ? 14 : 0;
    const mb = replyCount > 0 && !hideReplyChrome ? 18 : 0;
    const offset = (mt - mb) / 2;
    return (
      <span
        aria-hidden="true"
        className="hidden [@media(hover:none)]:block absolute -translate-y-1/2 text-[11px] leading-[14px] text-[rgba(60,60,67,0.6)] whitespace-nowrap pointer-events-none"
        style={{
          // Past the row's right edge AND past the list's 16px padding, since
          // overflow clips at the padding box.
          left: 'calc(100% + 16px)',
          width: 72,
          paddingLeft: 10,
          top: `calc(50% + ${offset}px)`,
        }}
      >
        {formatExactTime(new Date(message.createdAt))}
      </span>
    );
  };

  /** "N Replies" under the parent bubble. Opens the thread in focus mode.
   *  The label is 16px tall, so a transparent `after` expansion carries it to
   *  a 44px target without the label itself taking that much room. */
  const renderReplyCount = () => {
    if (hideReplyChrome || replyCount < 1) return null;
    return (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onOpenThread?.(message.id); }}
        disabled={!onOpenThread}
        className="ios-press absolute left-0 text-[12px] leading-[16px] font-medium text-biscayne
          after:absolute after:content-[''] after:-inset-x-2 after:-top-[14px] after:-bottom-[14px]"
        style={{ top: '100%', marginTop: 2 }}
      >
        {replyCount === 1 ? '1 Reply' : `${replyCount} Replies`}
      </button>
    );
  };

  // The Tapback pill, hung off the bubble's outward-facing top corner. Rendered
  // inside each bubble's own `relative` box so it tracks the bubble, and after
  // the corner radius rather than inside any overflow-hidden child.
  const renderReactionPill = () => {
    if (!message.reactions || Object.keys(message.reactions).length === 0) return null;
    return (
      <ReactionPill
        reactions={message.reactions}
        isOwnMessage={isOwnMessage}
      />
    );
  };

  // What the menu holds. Icons are drawn at 100% of their box so the size
  // decision stays with ContextMenu's --menu-icon token, which is 24 on touch
  // and 14 on pointer devices.
  const menuGroups = (): MenuGroup[] => {
    const actions: MenuItem[] = [];
    const destructive: MenuItem[] = [];
    const icon = (d: string) => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="w-full h-full" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d={d} />
      </svg>
    );

    // Reply is available on your own messages too. iOS lets you quote yourself,
    // and in an async coaching thread quoting your own earlier point is the
    // normal way to pick a conversation back up days later.
    if (onReply) {
      actions.push({
        key: 'reply',
        label: 'Reply',
        icon: icon('M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6'),
        onSelect: () => onReply(message),
      });
    }
    if (!isOwnMessage && onReplyWithVideoEdit && message.type === 'video' && message.videoUrl) {
      actions.push({
        key: 'videoedit',
        label: 'Edit & Reply',
        icon: icon('M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z'),
        onSelect: () => onReplyWithVideoEdit(message),
      });
    }
    if (canEdit && onEdit) {
      actions.push({
        key: 'edit',
        label: 'Edit',
        icon: icon('M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z'),
        onSelect: () => onEdit(message),
      });
    }
    if (canDelete && onDelete) {
      destructive.push({
        key: 'delete',
        label: 'Delete',
        icon: icon('M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16'),
        onSelect: () => onDelete(message),
        destructive: true,
      });
    }

    // Two groups, the way Mac Messages splits them: everything that acts on
    // the message together, and the destructive action alone below a
    // separator so it is never the row your thumb lands on by accident.
    return [
      { key: 'actions', items: actions },
      { key: 'destructive', items: destructive },
    ];
  };

  const renderMenuDropdown = () => (
    <div ref={menuHostRef}>
      <ContextMenu
        anchor={menuAnchor}
        onClose={closeMenu}
        ariaLabel="Message options"
        groups={menuGroups()}
        header={canReact ? (
          <ReactionPicker
            current={myReaction}
            onPick={(reaction) => { onReact(message, reaction); closeMenu(); }}
          />
        ) : undefined}
      />
    </div>
  );

  // Soft-deleted tombstone — rendered in place of any message type when
  // isDeleted is true. Preserves position / alignment in the thread.
  if (message.isDeleted) {
    return (
      <div className={`relative flex gap-2.5 items-end ${isOwnMessage ? 'flex-row-reverse' : 'flex-row'}`}>
        {renderRevealTime()}
        <div
          className="max-w-[70%] group relative"
          style={bubbleStyle}
          {...longPressHandlers}
        >
          <div
            className="border border-dashed border-gray-300"
            style={{
              borderRadius: isOwnMessage ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
              padding: '6px 12px',
              background: '#E9E9EB',
            }}
          >
            <p className="text-[12.5px] italic flex items-center gap-1.5 text-db-text-3">
              <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              This message was deleted
            </p>
          </div>
          <HoverTime iso={message.createdAt} isOwnMessage={isOwnMessage} isRevealed={isRevealed} />
        </div>
      </div>
    );
  }

  // Video modal overlay — rendered when play button is tapped on player submission
  const videoModal = videoModalUrl ? (
    <VideoModal videoUrl={videoModalUrl} onClose={() => setVideoModalUrl(null)} />
  ) : null;

  // Player video submitted (lesson_request) — compact secondary card.
  // Soft brand-teal wash + 3px stripe + thumbnail placeholder + content
  // stack + trailing check. Sent confirmation, not a primary read.
  if (message.type === 'lesson_request') {
    const cardLabel = lessonEventLabel({
      event: 'submitted',
      offeringType: lessonOrder?.type,
      lessonNumber: lesson?.lessonNumber,
      totalLessons: lessonOrder?.totalLessons,
    });
    const playerVideoUrl = lesson?.playerVideoUrl;
    const canOpen = onOpenLesson && lesson;
    const coachFirstName = (liveLessonCoach?.name ?? lesson?.coachName ?? lessonOrder?.coachName ?? 'Your coach')
      .split(' ').filter(Boolean)[0] || 'Your coach';
    // Per-lesson note typed by the player at upload. Cloud function writes
    // lesson.playerMessage for TP continuations; the initial purchase
    // (shot_consult / single_lesson / first TP lesson) carries it on the
    // order. Fallback chain covers both.
    const noteText = (lesson?.playerMessage ?? lessonOrder?.playerMessage ?? '').trim() || null;

    // Player view = isOwnMessage true (player IS the submitter).
    // Coach view = isOwnMessage false.
    let subline = '';
    if (isOwnMessage) {
      subline = `${coachFirstName} typically delivers within 1-2 days`;
    } else {
      subline = `Submitted ${formatRelativeTime(message.createdAt)}`;
    }

    return (
      <>
        {videoModal}
        <div className={`relative flex flex-col my-2 gap-1 ${isOwnMessage ? 'items-end' : 'items-start'}`}>
          {renderRevealTime()}
          {/* Cluster: sender chip (received side only, when note present)
              + note bubble + system card. Tight gap so they read as one
              unit. Sender chip is omitted on own-message side per spec. */}
          {!isOwnMessage && noteText && (
            <ClusterSenderChip
              avatarUrl={liveSender?.avatarUrl ?? message.senderAvatarUrl}
              name={liveSender?.name ?? message.senderName}
              isCoachSender={isCoachSender}
              onClick={onAvatarClick}
            />
          )}
          {noteText && (
            <NoteBubble text={noteText} isOwnMessage={isOwnMessage} />
          )}
          <div
            ref={bubbleRef}
            tabIndex={-1}
            className="max-w-[85%] md:max-w-[68%] min-w-[280px] group relative outline-none"
            style={bubbleStyle}
            {...longPressHandlers}
            onContextMenu={handleContextMenu}
            onClickCapture={swallowPostPressClick}
          >
            {renderReactionPill()}
            {renderReplyCount()}
            {renderMenuDropdown()}
            {/* Same treatment as the other three lesson cards: white, radius
                16, the shared shadow. The tinted wash and the 3px accent
                stripe were the last of the old card styling. */}
            <div
              className="flex overflow-hidden bg-white"
              style={{
                borderRadius: '16px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 4px 12px rgba(0,0,0,0.04)',
              }}
            >
              <div className="flex items-center gap-3 px-3 py-2.5 flex-1 min-w-0">
                {/* Thumbnail — real frame from the player's video via
                    client-side canvas extraction (VideoFrameThumb). Falls
                    back to the dark teal gradient + upload-arrow icon if
                    extraction fails or the URL is missing. */}
                <button
                  type="button"
                  onClick={() => {
                    if (isOwnMessage && playerVideoUrl) {
                      setVideoModalUrl(playerVideoUrl);
                    } else if (canOpen) {
                      onOpenLesson(lesson);
                    }
                  }}
                  className="w-11 h-11 rounded-lg flex-shrink-0 overflow-hidden cursor-pointer hover:opacity-90 transition-opacity"
                  aria-label="Open submission"
                >
                  <VideoFrameThumb
                    videoUrl={playerVideoUrl}
                    alt="Submitted video"
                    className="w-full h-full object-cover"
                    placeholder={
                      <div
                        className="w-full h-full flex items-center justify-center"
                        style={{ background: 'linear-gradient(135deg, #1A6B8A 0%, #0F4A63 100%)' }}
                      >
                        <svg
                          width="18" height="18" viewBox="0 0 24 24" fill="none"
                          stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                          <polyline points="17 8 12 3 7 8" />
                          <line x1="12" y1="3" x2="12" y2="15" />
                        </svg>
                      </div>
                    }
                  />
                </button>

                {/* Content stack — on mobile we strip the "Training
                    program · " prefix off the training_program headline so
                    it fits in the compact card width. md+ keeps the full
                    label since the chrome has room. */}
                <div className="flex-1 min-w-0">
                  <div className="text-[14px] font-medium text-db-text-1 truncate leading-tight">
                    <span className="md:hidden">{cardLabel.replace(/^Training program · /, '')}</span>
                    <span className="hidden md:inline">{cardLabel}</span>
                  </div>
                  <div className="text-[12px] text-db-text-2 leading-snug mt-0.5 truncate" style={{ opacity: 0.85 }}>{subline}</div>
                  {/* Optional player caption — kept available for context */}
                  {message.text && !message.text.match(/^Lesson requested:/i) && (
                    <p className="text-[12px] text-db-text-2 mt-1 line-clamp-2 leading-snug">{message.text}</p>
                  )}
                </div>

                {/* Trailing check — reinforces sent state */}
                <div className="flex-shrink-0" aria-hidden="true">
                  <svg
                    width="14" height="14" viewBox="0 0 24 24" fill="none"
                    stroke="#1A6B8A" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </div>
              </div>
            </div>
            <HoverTime iso={message.createdAt} isOwnMessage={isOwnMessage} isRevealed={isRevealed} />
          </div>
        </div>
      </>
    );
  }

  // Coach lesson delivered (lesson_delivered) — HERO card.
  // Netflix-episode / MasterClass treatment: 16:9 thumbnail with play
  // overlay + duration badge + coach name overlay, white content area
  // with headline / subline / full-width CTA. Drops the side avatar in
  // favor of in-thumbnail coach identity.
  if (message.type === 'lesson_delivered') {
    const subline = lessonEventLabel({
      event: 'delivered',
      offeringType: lessonOrder?.type,
      lessonNumber: lesson?.lessonNumber,
      totalLessons: lessonOrder?.totalLessons,
    }).replace(/\s+delivered$/, '');  // strip trailing "delivered" — context already conveyed by headline
    const canOpen = onOpenLesson && lesson;
    const coachAvatarUrl = liveLessonCoach?.avatarUrl ?? lesson?.coachAvatarUrl ?? lessonOrder?.coachAvatarUrl ?? message.senderAvatarUrl;
    const coachDisplay = liveLessonCoach?.name ?? lesson?.coachName ?? lessonOrder?.coachName ?? message.senderName ?? 'Coach';
    const coachFirstName = coachDisplay.split(' ').filter(Boolean)[0] || coachDisplay;
    const playerFirstName = (liveLessonPlayer?.name ?? lessonOrder?.playerName ?? 'Player').split(' ').filter(Boolean)[0] || 'Player';
    const headline = isOwnMessage ? 'Breakdown sent' : 'Your breakdown is ready';
    const durationLabel = formatVideoDuration(lesson?.coachResponseVideoDuration);

    // Thumbnail source: the delivered video the player watches is the
    // player's own uploaded video with the coach's edits rendered at
    // playback (no separate baked output file). Same source = same
    // thumbnail field as the submitted card; the first ~1s frame
    // represents the lesson content for both sides.
    const deliveredThumbUrl = lesson?.playerVideoUrl;
    // Coach's player-visible breakdown note. Despite the field name,
    // `Lesson.coachNotes` is the PLAYER-VISIBLE note (see
    // services/lessonSubmission.ts:138 where it's written from
    // `notes ?? savedDraft.notes` — the editor's "Visible to player"
    // field). The truly private notes live only on the draft and never
    // propagate to the lesson doc, so we're safe reading this field.
    const noteText = (lesson?.coachNotes ?? '').trim() || null;

    return (
      <div className={`relative flex flex-col my-2 gap-1 ${isOwnMessage ? 'items-end' : 'items-start'}`}>
        {renderRevealTime()}
        {/* Cluster: sender chip (received side only, when note present)
            + note bubble + hero card. Sender chip omitted for own
            messages per spec. */}
        {!isOwnMessage && noteText && (
          <ClusterSenderChip
            avatarUrl={liveSender?.avatarUrl ?? message.senderAvatarUrl}
            name={liveSender?.name ?? message.senderName}
            isCoachSender={isCoachSender}
            onClick={onAvatarClick}
          />
        )}
        {noteText && (
          <NoteBubble text={noteText} isOwnMessage={isOwnMessage} />
        )}
        <div
          ref={bubbleRef}
          tabIndex={-1}
          className="max-w-[85%] md:max-w-[320px] min-w-[280px] w-full group relative outline-none"
          style={bubbleStyle}
          {...longPressHandlers}
          onContextMenu={handleContextMenu}
          onClickCapture={swallowPostPressClick}
        >
          {renderReactionPill()}
          {renderReplyCount()}
          {renderMenuDropdown()}
          <div
            className="overflow-hidden bg-white"
            style={{
              borderRadius: '16px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 4px 12px rgba(0,0,0,0.04)',
            }}
          >
            {/* THUMBNAIL — real frame via VideoFrameThumb against the
                player's video URL. Falls back to the dark-teal gradient +
                centered play overlay when extraction fails (CORS, codec,
                missing URL). Aspect 16/10 keeps the card from growing
                too tall at the wider max-width. */}
            <button
              type="button"
              onClick={() => canOpen && onOpenLesson(lesson)}
              disabled={!canOpen}
              className="relative w-full block overflow-hidden group/thumb"
              style={{
                aspectRatio: '16 / 10',
                cursor: canOpen ? 'pointer' : 'default',
              }}
              aria-label="Watch lesson"
            >
              {/* Thumbnail layer: pre-generated server frame when available,
                  client-side extraction otherwise, gradient as last resort. */}
              {preGenThumbUrl ? (
                <img
                  src={preGenThumbUrl}
                  alt=""
                  className="absolute inset-0 w-full h-full object-cover"
                />
              ) : (
                <VideoFrameThumb
                  videoUrl={deliveredThumbUrl}
                  alt=""
                  className="absolute inset-0 w-full h-full object-cover"
                  placeholder={
                    <div
                      className="absolute inset-0"
                      style={{ background: 'linear-gradient(135deg, #1A6B8A 0%, #0F4A63 100%)' }}
                    />
                  }
                />
              )}
              {/* Bottom gradient for overlay text legibility */}
              <div
                className="absolute inset-x-0 bottom-0 h-[45%] pointer-events-none"
                style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.6) 100%)' }}
              />
              {/* Play button — center */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div
                  className="w-14 h-14 rounded-full flex items-center justify-center transition-transform group-hover/thumb:scale-105"
                  style={{
                    background: '#FFFFFF',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="#1A6B8A" aria-hidden="true">
                    <polygon points="6 4 20 12 6 20 6 4" />
                  </svg>
                </div>
              </div>
              {/* Duration badge — top-right */}
              {durationLabel && (
                <div
                  className="absolute top-2.5 right-2.5 px-2 py-0.5 rounded-full"
                  style={{ background: 'rgba(0,0,0,0.55)' }}
                >
                  <span className="text-[11px] font-semibold text-white">{durationLabel}</span>
                </div>
              )}
              {/* Coach identity — bottom-left */}
              <div className="absolute bottom-2.5 left-2.5 flex items-center gap-1.5">
                <div className="w-5 h-5 rounded-full overflow-hidden flex-shrink-0" style={{ boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.25)' }}>
                  {coachAvatarUrl ? (
                    <img src={coachAvatarUrl} alt={coachFirstName} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.15)' }}>
                      <span className="text-[9px] font-bold text-white">{coachFirstName.charAt(0).toUpperCase()}</span>
                    </div>
                  )}
                </div>
                <span className="text-[12px] font-semibold text-white">{coachFirstName}</span>
              </div>
            </button>

            {/* CONTENT — tight stack (no filler body line). message.text
                is the auto-generated "Your lesson is ready!" string for
                lesson_delivered messages — dropped intentionally; coach
                notes will surface via lesson.coachNotes in a later batch. */}
            <div className="px-3 py-3 flex flex-col gap-1.5">
              <div className="text-[15px] font-semibold text-db-text-1 leading-tight">{headline}</div>
              <div className="text-[12.5px] text-db-text-2 leading-snug">
                {isOwnMessage ? `To ${playerFirstName} · ${subline}` : subline}
              </div>
              {lesson?.attachedDrills && lesson.attachedDrills.length > 0 && (() => {
                const allDrills = lesson.attachedDrills!;
                const MAX_VISIBLE = 4;
                const visible = allDrills.slice(0, MAX_VISIBLE);
                const overflow = allDrills.length - MAX_VISIBLE;
                return (
                  <div className="flex items-end gap-1.5 mt-1 flex-wrap">
                    <span className="text-[11px] font-semibold text-db-text-3 uppercase tracking-wide flex-shrink-0">
                      {allDrills.length === 1 ? 'Practice drill' : 'Practice drills'}
                    </span>
                    {visible.map((drill) => (
                      <div key={drill.drillId} title={drill.title} className="w-8 h-8 rounded-md overflow-hidden flex-shrink-0 bg-db-bg flex items-center justify-center">
                        {drill.thumbnailUrl ? (
                          <img src={drill.thumbnailUrl} alt={drill.title} className="w-full h-full object-cover" />
                        ) : (
                          <VideoFrameThumb
                            videoUrl={drill.videoUrl || null}
                            alt={drill.title}
                            className="w-full h-full object-cover"
                            placeholder={
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="var(--color-db-text-3)" aria-hidden="true">
                                <polygon points="6 4 20 12 6 20 6 4" />
                              </svg>
                            }
                          />
                        )}
                      </div>
                    ))}
                    {overflow > 0 && (
                      <span className="text-[11px] font-medium text-db-text-3">+{overflow}</span>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
          <HoverTime iso={message.createdAt} isOwnMessage={isOwnMessage} isRevealed={isRevealed} />
        </div>
      </div>
    );
  }

  // Progress report delivered — sits directly below the lesson_delivered
  // hero card on the final-lesson submit. Same outer card frame as the
  // lesson_delivered branch (max-w, rounded-[16px], soft shadow, per-bubble
  // padding) but the inside is a different shape: coach avatar on the left,
  // title + subtitle stack, full-width "View Progress Report" CTA below.
  // Warm-amber accent throughout so it's visually distinct from the teal
  // lesson breakdown card while staying inside chat rhythm.
  if (message.type === 'progress_report_delivered') {
    const coachAvatarUrl = liveLessonCoach?.avatarUrl ?? lesson?.coachAvatarUrl ?? lessonOrder?.coachAvatarUrl ?? message.senderAvatarUrl;
    const coachDisplay = liveLessonCoach?.name ?? lesson?.coachName ?? lessonOrder?.coachName ?? message.senderName ?? 'Coach';
    const coachFirstName = capName(coachDisplay.split(' ').filter(Boolean)[0] || coachDisplay);
    const playerFirstName = capName((liveLessonPlayer?.name ?? lessonOrder?.playerName ?? 'Player').split(' ').filter(Boolean)[0] || 'Player');
    const totalLessons = lessonOrder?.totalLessons ?? 4;
    // Coach side reads "To {player}"; player side reads "From {coach}".
    const subtitle = isOwnMessage
      ? `To ${playerFirstName} · Training program · ${totalLessons} lessons`
      : `From ${coachFirstName} · Training program · ${totalLessons} lessons`;
    const orderId = lessonOrder?.id;
    const canOpen = !!orderId;

    return (
      <div className={`relative flex flex-col my-2 gap-1 ${isOwnMessage ? 'items-end' : 'items-start'}`}>
        {renderRevealTime()}
        <div
          ref={bubbleRef}
          tabIndex={-1}
          className="max-w-[85%] md:max-w-[320px] min-w-[280px] w-full group relative outline-none"
          style={bubbleStyle}
          {...longPressHandlers}
          onContextMenu={handleContextMenu}
          onClickCapture={swallowPostPressClick}
        >
          {renderReactionPill()}
          {renderReplyCount()}
          {renderMenuDropdown()}
          <div
            className="overflow-hidden bg-white"
            style={{
              borderRadius: '16px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 4px 12px rgba(0,0,0,0.04)',
            }}
          >
            <div className="px-3 py-3 flex flex-col gap-2.5">
              {/* Avatar-left + title/subtitle stack row */}
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-full overflow-hidden flex-shrink-0 border border-db-border bg-db-oasis">
                  {coachAvatarUrl ? (
                    <img src={coachAvatarUrl} alt={coachDisplay} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <span className="text-[12px] font-bold text-white">
                        {coachDisplay.split(' ').filter(Boolean).map(w => w[0]).slice(0, 2).join('').toUpperCase()}
                      </span>
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[15px] font-semibold text-db-text-1 leading-tight">Progress Report</div>
                  <div className="text-[12.5px] text-db-text-2 leading-snug mt-0.5 truncate">{subtitle}</div>
                </div>
              </div>

              {/* Full-width CTA */}
              <button
                onClick={() => { if (canOpen && orderId) navigate(`/progress-report/${orderId}`); }}
                disabled={!canOpen}
                className="mt-1 w-full py-2.5 rounded-[10px] text-[13.5px] font-semibold text-white bg-[#A8743C] hover:bg-[#8E6232] btn-press transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                View Progress Report
              </button>
            </div>
          </div>
          <HoverTime iso={message.createdAt} isOwnMessage={isOwnMessage} isRevealed={isRevealed} />
        </div>
      </div>
    );
  }

  // Double Bagel notification card
  if (isDoubleBagelSender && message.type === 'text') {
    return (
      <div className="relative flex gap-2.5 items-end">
        {renderRevealTime()}
        <div
          className="max-w-[70%] group relative outline-none"
          ref={bubbleRef}
          tabIndex={-1}
          style={bubbleStyle}
          {...longPressHandlers}
          onContextMenu={handleContextMenu}
          onClickCapture={swallowPostPressClick}
        >
          {/* Standard incoming bubble, same as any other text message: §8 has
              one treatment and support is not an exception to it. Tail on the
              last of a run, via the same showAvatar flag. */}
          <div
            className="relative"
            style={{
              borderRadius: showAvatar ? '18px 18px 18px 4px' : 18,
              padding: '6px 12px',
              background: '#E9E9EB',
              color: '#000000',
            }}
          >
            {renderReactionPill()}
            {renderReplyCount()}
            {message.category && (
              <div className="text-[11px] font-bold uppercase tracking-[.06em] text-[rgba(60,60,67,0.6)] mb-1">
                {message.category}
              </div>
            )}
            {isEditing ? (
              <div>
                <textarea
                  value={draftText}
                  onChange={(e) => setDraftText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onEditSave?.(draftText.trim()); }
                    if (e.key === 'Escape') onEditCancel?.();
                  }}
                  className="w-full bg-transparent text-[17px] leading-[22px] md:text-[13px] md:leading-[18px] text-black outline-none resize-none placeholder:text-[rgba(60,60,67,0.3)]"
                  rows={Math.max(2, draftText.split('\n').length)}
                  autoFocus
                />
                <div className="flex justify-end gap-1.5 mt-2">
                  <button
                    type="button"
                    onClick={onEditCancel}
                    className="text-[11px] font-medium px-2.5 py-1 rounded-lg text-db-text-2 hover:bg-db-bg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => onEditSave?.(draftText.trim())}
                    disabled={!draftText.trim() || draftText.trim() === message.text}
                    className="text-[11px] font-semibold px-2.5 py-1 rounded-lg bg-biscayne text-white hover:opacity-90 transition-colors disabled:opacity-40"
                  >
                    Save
                  </button>
                </div>
              </div>
            ) : (
              <>
                <p className="text-[17px] leading-[22px] md:text-[13px] md:leading-[18px] whitespace-pre-wrap break-words">{message.text}</p>
                {message.editedAt && (
                  <span className="text-[12px] leading-[16px] mt-0.5 block text-[rgba(60,60,67,0.6)]">edited</span>
                )}
              </>
            )}
          </div>
          {renderMenuDropdown()}
          {/* Staff attribution stays inline (debug/audit info for the support
              admin view); per-bubble timestamp moved to the hover reveal. */}
          {showStaffAttribution && message.sentByStaffName && (
            <div className="text-[10px] text-db-text-3 mt-1 pl-1">
              {message.sentByStaffName}
            </div>
          )}
          <HoverTime iso={message.createdAt} isOwnMessage={false} isRevealed={isRevealed} />
        </div>
      </div>
    );
  }

  // System messages
  if (message.type === 'system') {
    return (
      /* Centred Footnote, no pill and no fill — how iOS renders a system
         notice. The old bg-gray-100 / text-gray-600 / text-sm was raw
         Tailwind, off §8.7's palette and off §8.6's scale. */
      <div className="flex justify-center my-4">
        <div className="max-w-md px-4 text-center text-[13px] leading-[18px] text-[rgba(60,60,67,0.6)]">
          {message.text}
        </div>
      </div>
    );
  }

  // Video messages
  if (message.type === 'video') {
    return (
      /* The ghost and connector are siblings of the message row, not children
         of the bubble wrapper: they follow the parent's side, which may be the
         opposite one, and the wrapper cannot reach across the pane. */
      <div className="flex flex-col">
      {renderReplyGhost()}
      <div className={`relative flex gap-2.5 items-end ${isOwnMessage ? 'flex-row-reverse' : 'flex-row'}`}>
        {renderRevealTime()}
        <div
          className="flex flex-col max-w-[220px] group relative outline-none"
          ref={bubbleRef}
          tabIndex={-1}
          style={bubbleStyle}
          {...longPressHandlers}
          onContextMenu={handleContextMenu}
          onClickCapture={swallowPostPressClick}
        >
          <div className="relative">
            {renderReactionPill()}
            {renderReplyCount()}
            <div className="rounded-xl overflow-hidden">

            {message.videoUrl && (
              <div className="relative group">
                <video
                  src={message.videoUrl}
                  controls
                  controlsList="nodownload"
                  preload="metadata"
                  className="w-full"
                  style={{ maxHeight: '400px' }}
                  /* No crossOrigin: display-only. CORS-mode loading fails on
                     iPhone Safari against our bucket (Content-Range not
                     exposed) — see the same removal on LessonDeliveredDetail. */
                  onError={(e) => {
                    console.error('Video playback error:', e);
                    console.error('Video URL:', message.videoUrl);
                  }}
                />
                {/* Download button */}
                <button
                  onClick={() => downloadVideo(
                    message.videoUrl!,
                    `video-${message.id}.mp4`
                  )}
                  className="absolute top-2 right-2 bg-black/60 hover:bg-black/80 text-white p-2 rounded-full transition-opacity opacity-0 group-hover:opacity-100"
                  title="Download video"
                  aria-label="Download video"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                </button>
              </div>
            )}

            {/* Optional text caption below video */}
            {message.text && (
              <div
                className={`px-3 py-2 ${isOwnMessage ? 'text-white' : 'bg-white'}`}
                style={isOwnMessage ? { background: '#1A6B8A' } : {}}
              >
                <p className="text-[13px] whitespace-pre-wrap break-words" style={isOwnMessage ? { color: 'white' } : { color: '#1A1A1A' }}>{message.text}</p>
              </div>
            )}
            </div>{/* end rounded-xl overflow-hidden */}
          </div>{/* end relative wrapper */}
          {renderMenuDropdown()}
          <HoverTime iso={message.createdAt} isOwnMessage={isOwnMessage} isRevealed={isRevealed} />
        </div>
      </div>
      </div>
    );
  }

  // Drill attachment card — coach-sent drill shared directly in conversation
  if (message.type === 'drill_attachment') {
    const drillShotTypeColors: Record<string, string> = {
      forehand: '#3B82F6', backhand: '#8B5CF6', serve: '#F97316',
      volley: '#22C55E', return: '#06B6D4', movement: '#F59E0B', general: '#9CA3AF',
    };
    const accentColor = drillShotTypeColors[message.drillShotType ?? ''] ?? '#6366F1';
    return (
      <>
        {videoModal}
        <div className={`relative flex gap-2.5 items-end ${isOwnMessage ? 'flex-row-reverse' : 'flex-row'}`}>
          {renderRevealTime()}
          <div
            ref={bubbleRef}
            tabIndex={-1}
            className="group relative outline-none max-w-[260px]"
            data-testid="drill-attachment-card"
            style={bubbleStyle}
            {...longPressHandlers}
            onContextMenu={handleContextMenu}
            onClickCapture={swallowPostPressClick}
          >
            {renderReactionPill()}
            {renderReplyCount()}
            {renderMenuDropdown()}
            <div
              className="rounded-xl overflow-hidden shadow-sm border border-gray-200 bg-white"
              style={{ borderLeft: `3px solid ${accentColor}` }}
            >
              {/* Thumbnail strip */}
              {message.drillThumbnailUrl ? (
                <div className="h-28 bg-black overflow-hidden">
                  <img
                    src={message.drillThumbnailUrl}
                    alt={message.drillTitle}
                    className="w-full h-full object-cover opacity-90"
                  />
                </div>
              ) : (
                <div className="h-20 flex items-center justify-center" style={{ background: '#F3F4F6' }}>
                  <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="m15.75 10.5 4.72-4.72a.75.75 0 0 1 1.28.53v11.38a.75.75 0 0 1-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 0 0 2.25-2.25v-9a2.25 2.25 0 0 0-2.25-2.25h-9A2.25 2.25 0 0 0 2.25 7.5v9a2.25 2.25 0 0 0 2.25 2.25Z" />
                  </svg>
                </div>
              )}

              {/* Content */}
              <div className="px-3 pt-2 pb-2.5">
                <div className="flex items-center gap-1.5 mb-1">
                  {message.drillShotType && (
                    <span
                      className="text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full"
                      style={{ background: `${accentColor}20`, color: accentColor }}
                    >
                      {message.drillShotType}
                    </span>
                  )}
                  <span className="text-[10px] text-gray-400 font-medium">Drill</span>
                </div>
                <p className="text-sm font-semibold text-gray-900 leading-tight truncate">{message.drillTitle ?? 'Drill'}</p>
                {message.drillDescription && (
                  <p className="text-xs text-gray-500 line-clamp-2 mt-0.5">{message.drillDescription}</p>
                )}
                {message.drillVideoUrl && (
                  <button
                    onClick={() => setVideoModalUrl(message.drillVideoUrl!)}
                    data-testid="watch-drill-btn"
                    className="mt-2 flex items-center gap-1 text-xs font-semibold hover:opacity-80 transition-opacity"
                    style={{ color: accentColor }}
                  >
                    <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
                    Watch Drill
                  </button>
                )}
              </div>
            </div>
            <HoverTime iso={message.createdAt} isOwnMessage={isOwnMessage} isRevealed={isRevealed} />
          </div>
        </div>
      </>
    );
  }

  // Pro video attachment card — coach-sent pro player clip shared in conversation
  if (message.type === 'pro_video_attachment') {
    return (
      <>
        {videoModal}
        <div className="flex flex-col">
          {renderReplyGhost()}
          <div className={`relative flex gap-2.5 items-end ${isOwnMessage ? 'flex-row-reverse' : 'flex-row'}`}>
            {renderRevealTime()}
            <div
              ref={bubbleRef}
              tabIndex={-1}
              className="group relative outline-none max-w-[260px]"
              data-testid="pro-video-attachment-card"
              style={bubbleStyle}
              {...longPressHandlers}
              onContextMenu={handleContextMenu}
              onClickCapture={swallowPostPressClick}
            >
              {renderReactionPill()}
              {renderReplyCount()}
              {renderMenuDropdown()}
              {/* Section 8 card surface: white, radius 16, the shared shadow.
                  No left accent stripe and no grey border — the shadow is the
                  elevation, the same as the lesson cards. */}
              <div
                className="overflow-hidden bg-white"
                style={{
                  borderRadius: 16,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 4px 12px rgba(0,0,0,0.04)',
                }}
              >
                {/* Thumbnail strip */}
                {message.proVideoThumbnailUrl ? (
                  <div className="h-28 bg-black overflow-hidden">
                    <img
                      src={message.proVideoThumbnailUrl}
                      alt={message.proVideoTitle}
                      className="w-full h-full object-cover opacity-90"
                    />
                  </div>
                ) : (
                  <div className="h-20 flex items-center justify-center" style={{ background: '#F2F2F7' }}>
                    <FilmSlate size={28} color="rgba(60,60,67,0.3)" aria-hidden="true" />
                  </div>
                )}

                {/* Content */}
                <div className="px-3 pt-2 pb-2.5">
                  {/* Caption 2 11/13, secondary. */}
                  <div className="text-[11px] leading-[13px] font-medium uppercase tracking-[.06em] text-[rgba(60,60,67,0.6)] mb-1">
                    Pro Video
                  </div>
                  {/* Subhead 15/20, body colour. */}
                  <p className="text-[15px] leading-[20px] font-semibold text-[#1C1C1C] truncate">
                    {message.proVideoTitle ?? 'Pro Video'}
                  </p>

                  {/* Icon-plus-text meta rows, the Discover pattern, in place
                      of the tinted pills. Footnote 13/18. */}
                  {(message.proVideoPlayerName || (message.proVideoTags && message.proVideoTags.length > 0)) && (
                    <div className="mt-1.5 flex flex-col gap-1">
                      {message.proVideoPlayerName && (
                        <div className="flex items-center gap-1.5">
                          <User size={14} weight="bold" color="#1A6B8A" className="flex-shrink-0" aria-hidden="true" />
                          <span className="text-[13px] leading-[18px] text-[#1C1C1C] truncate">
                            {message.proVideoPlayerName}
                          </span>
                        </div>
                      )}
                      {message.proVideoTags && message.proVideoTags.length > 0 && (
                        <div className="flex items-center gap-1.5">
                          <Tag size={14} weight="bold" color="#54C3EA" className="flex-shrink-0" aria-hidden="true" />
                          <span className="text-[13px] leading-[18px] text-[#1C1C1C] truncate">
                            {message.proVideoTags.join(', ')}
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  {message.proVideoUrl && (
                    <button
                      onClick={() => setVideoModalUrl(message.proVideoUrl!)}
                      data-testid="watch-pro-video-btn"
                      className="ios-press mt-2 flex items-center gap-1 text-[13px] leading-[18px] font-semibold text-biscayne"
                    >
                      <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5v14l11-7z"/></svg>
                      Watch Video
                    </button>
                  )}
                </div>
              </div>
              <HoverTime iso={message.createdAt} isOwnMessage={isOwnMessage} isRevealed={isRevealed} />
            </div>
          </div>
        </div>
      </>
    );
  }

  // Regular text messages
  return (
    /* Messages shows no avatar on the outgoing side of a one-to-one thread,
       so the sender's own bubble sits flush to the right edge. */
    <div className="flex flex-col">
    {/* Ghost and connector sit outside the message row: they follow the
        parent's side, which may be the opposite one, and the bubble wrapper
        shrink-wraps to one side of the pane. */}
    {renderReplyGhost()}
    <div className={`relative flex gap-2.5 items-end ${isOwnMessage ? 'flex-row-reverse' : 'flex-row'}`}>
      {renderRevealTime()}
      <div
        className="max-w-[70%] group relative outline-none"
        ref={bubbleRef}
        tabIndex={-1}
        style={bubbleStyle}
        {...longPressHandlers}
        onContextMenu={handleContextMenu}
        onClickCapture={swallowPostPressClick}
      >
        <div
          className="relative"
          style={{
            // Tail on the last bubble of a consecutive run, drawn by dropping
            // the corner radius on the sender's side. `showAvatar` is already
            // "last in a same-sender streak" — the flag outlived the avatar it
            // was named for.
            borderRadius: showAvatar
              ? (isOwnMessage ? '18px 18px 4px 18px' : '18px 18px 18px 4px')
              : 18,
            padding: '6px 12px',
            background: isOwnMessage ? '#1A6B8A' : '#E9E9EB',
            color: isOwnMessage ? '#FFFFFF' : '#000000',
          }}
        >
          {renderReactionPill()}
          {renderReplyCount()}
          {isEditing ? (
            <div>
              <textarea
                value={draftText}
                onChange={(e) => setDraftText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onEditSave?.(draftText.trim()); }
                  if (e.key === 'Escape') onEditCancel?.();
                }}
                className={`w-full bg-transparent text-[17px] leading-[22px] md:text-[13px] md:leading-[18px] outline-none resize-none ${
                  isOwnMessage ? 'text-white placeholder:text-white/50' : 'text-black placeholder:text-[rgba(60,60,67,0.6)]'
                }`}
                rows={Math.max(2, draftText.split('\n').length)}
                autoFocus
              />
              <div className="flex justify-end gap-1.5 mt-2">
                <button
                  type="button"
                  onClick={onEditCancel}
                  className={`text-[11px] font-medium px-2.5 py-1 rounded-lg transition-colors ${
                    isOwnMessage
                      ? 'text-white/70 hover:bg-white/10'
                      : 'text-db-text-2 hover:bg-db-bg'
                  }`}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => onEditSave?.(draftText.trim())}
                  disabled={!draftText.trim() || draftText.trim() === message.text}
                  className={`text-[11px] font-semibold px-2.5 py-1 rounded-lg transition-colors disabled:opacity-40 ${
                    isOwnMessage
                      ? 'bg-white/20 text-white hover:bg-white/30'
                      : 'bg-biscayne text-white hover:opacity-90'
                  }`}
                >
                  Save
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* iOS runs 17/22; Mac Messages runs 13/18. md is 768, the same
                  boundary ResponsiveContext uses for isMobile. */}
              <p className="text-[17px] leading-[22px] md:text-[13px] md:leading-[18px] whitespace-pre-wrap break-words">{message.text}</p>
              {message.editedAt && (
                <span className={`text-[10px] mt-0.5 block ${isOwnMessage ? 'text-white/40' : 'text-db-text-3'}`}>
                  edited
                </span>
              )}
            </>
          )}
        </div>
        {renderMenuDropdown()}
        <HoverTime iso={message.createdAt} isOwnMessage={isOwnMessage} isRevealed={isRevealed} />
      </div>
    </div>
    </div>
  );
}
